import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';

import { addLeadingCharacter } from '../../../lib/time-formatters';
import { TimeSelector } from './TimeSelector';
import {
  getPreferredTimeFormat,
  timeTo12HourFormat,
  timeTo24HourFormat,
} from '../../../lib/utils/Convertor';
import { KeyboardKeys } from '../../../assets/enums/keyboard-keys.enum';

import type { TypeTime } from '../../../app/entities/strip';

import styles from './InlineTimeSelector.module.scss';
interface InlineTimeSelectorProps {
  time: {
    hour: number;
    minute: number;
    period?: 'am' | 'pm';
  };
  disabled?: boolean;
  onChange?: (time: TypeTime) => void;
  textToDisplay?: string;
  backgroundVisible?: boolean;
  style?: React.CSSProperties;
  /** Forwarded to the rendered TimeDisplay so a parent can style it. */
  className?: string;
}

export const InlineTimeSelector: React.FC<InlineTimeSelectorProps> = observer(
  ({
    time = { hour: 0, minute: 0 },
    disabled,
    onChange,
    textToDisplay,
    backgroundVisible,
    style = {},
    className,
  }) => {
    const [showTimeSelector, setShowTimeSelector] = useState(false);

    const handleTimeChange = async (value: TypeTime) => {
      if (onChange) {
        await onChange(value);
      }
      setShowTimeSelector(false);
    };

    let callTime = time;

    if (time && !time?.period) {
      callTime = timeTo12HourFormat(time);
    }

    const is24HourFormat = getPreferredTimeFormat() === '24h';

    const displayTime: InlineTimeSelectorProps['time'] = is24HourFormat
      ? timeTo24HourFormat(callTime)
      : callTime;

    const { hour, minute, period } = displayTime;
    const renderedTime = `${addLeadingCharacter(
      hour.toString(),
      is24HourFormat ? 2 : 1,
    )}:${addLeadingCharacter(minute.toString(), 2)}`;

    return (
      <>
        <TimeDisplay
          className={className}
          data-background-visible={backgroundVisible}
          {...(!disabled && { tabIndex: 0 })}
          onKeyDown={(e) => {
            if (disabled) {
              return;
            }

            if (e.key === KeyboardKeys.Enter) {
              setShowTimeSelector((prev) => !prev);
            }
          }}
          onClick={() => {
            if (disabled) {
              return;
            }

            setShowTimeSelector((prev) => !prev);
          }}
          style={style}
          data-disabled={disabled}
        >
          {textToDisplay}
          {renderedTime}
          {period && <span>{period}</span>}
        </TimeDisplay>

        {showTimeSelector && (
          <TimeSelector
            onTimeChange={handleTimeChange}
            time={callTime}
            onCancel={() => setShowTimeSelector(false)}
          />
        )}
      </>
    );
  },
);

export const TimeDisplay = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['time-display']} ${className}` : styles['time-display']}
    {...props}
  />
);
